/*
  consolidator.js — Multi-file merger

  Takes multiple parsed AIS datasets (one array per file) and merges
  them into one clean, deduplicated, chronologically sorted array.

  This is what makes multi-day date ranges work seamlessly.
  The user uploads 7 files for 7 days — this module stitches them
  into one unified vessel record as if it were a single dataset.

  Steps:
  1. Flatten all arrays into one
  2. Remove duplicate position reports (same MMSI + same timestamp)
  3. Sort everything by timestamp oldest first
*/

export function consolidate(arrayOfArrays) {
  if (!arrayOfArrays || arrayOfArrays.length === 0) return [];

  // Step 1 — flatten all file results into one big array
  const flat = arrayOfArrays.flat();

  if (flat.length === 0) return [];

  // Step 2 — deduplicate
  // Two records are duplicates if they share the same MMSI and timestamp.
  // This can happen when NOAA files have overlapping coverage at boundaries.
  const seen = new Set();
  const deduped = flat.filter((record) => {
    const key = `${record.mmsi}__${record.timestamp}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // Step 3 — sort by timestamp so tracks draw in the correct order
  deduped.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  return deduped;
}

/*
  filterByDateRange — narrows a consolidated dataset to a specific date range.

  startDate: JS Date object (start of range, inclusive)
  endDate: JS Date object (end of range, inclusive)
  records: consolidated AIS array

  Returns only the records that fall within the range.
  If no dates provided, returns all records unchanged.
*/
export function filterByDateRange(records, startDate, endDate) {
  if (!startDate && !endDate) return records;

  return records.filter((record) => {
    const t = new Date(record.timestamp);
    if (startDate && t < startDate) return false;
    if (endDate && t > endDate) return false;
    return true;
  });
}
